/**
 * @module App
 * @description Our NextJS App
 */
import { useContext, useEffect } from "react";
import { useRouter } from "next/router";
import { useReportWebVitals } from "next/web-vitals";
import { Provider as ReduxProvider } from "react-redux";
import makeStore from "@/redux/setup/store";
import { setMonarchMonitoring } from "@red-digital/bricks";
import { withContentProvider } from "@/providers/content-provider";
import { FuelContext, withFuel } from "@/providers/fuel-provider";
import { ready } from "@/js/utils/events";
import listOfMonarchComponents from "../monarch/listOfMonarchComponents";
import { useCohesionEvents } from "@/hooks/useCohesionEvents";
import { useScrollTracking } from "@/hooks/useScrollTracking";
import { CohesionSnippet } from "@redventures/cohesion-utils-react";
import { cohesionSnippet } from "@/js/config/cohesion";
import { withCohesion } from "@/providers/cohesion-provider";
import Localize from "@/resources/Localize";
import { spanishPreferred } from "@/js/utils/spanishPreferred";
import sendToAnalytics from "@/js/plugins/cwv.js";

import "@/fonts/icomoon/style.css";
import "@/styles/globals.css";
import "@/styles/bricks/all.scss";
import useLogRocket from "@/hooks/useLogrocket";

import Chat from "@/blocks/Chat";

const store = makeStore();

/**
 * @function App
 * @description Our NextJS App
 */
const App = ({ Component, pageProps }) => {
  const router = useRouter();

  useCohesionEvents();
  useScrollTracking();

  /**
   * @function loadJS
   * @description Load our global js
   */
  const loadJS = () =>
    import("@/js/main.js").then((module) => module.default());

  /**
   * @function useEffect
   * @description Tagular Page View, Replace Fuse numbers
   */
  useEffect(() => {
    router.events.on("routeChangeComplete", () => {
      tagular("pageView");

      if (window && window.newrelic) {
        window.newrelic.interaction().save();
      }
    });
  }, [router.events]);

  /**
   * @function useEffect
   * @description Load a11y conditional
   */
  useEffect(() => {
    if (process.env.environment === "local" || "a11y" in router.query) {
      import("@khanacademy/tota11y");
    }
  }, [router.query]);

  /**
   * @function useEffect
   * @description Kick off this modules functions
   */
  useEffect(() => {
    setMonarchMonitoring({ hasUpdatedSnippet: true });
    ready(loadJS);
  }, []);

  const { logRocketEnabled } = useContext(FuelContext);

  // Send CWV metrics to Make
  useReportWebVitals(sendToAnalytics);
  useLogRocket(logRocketEnabled);

  return (
    <ReduxProvider store={store}>
      <CohesionSnippet
        config={cohesionSnippet}
        src="https://cdn.cohesionapps.com/cohesion/cohesion-latest.min.js"
      />
      <Component {...pageProps} />
      <Chat />

      {/* LocalizeJS script (needs to be in here to fire correctly) */}
      {spanishPreferred() && process.env.environment === "production" && (
        <Localize />
      )}
    </ReduxProvider>
  );
};

const appHOC1 = withContentProvider({ App });
const appHOC2 = withFuel({ App: appHOC1 });
const appHOC3 = withCohesion({ App: appHOC2, assets: listOfMonarchComponents });

export default appHOC3;
